import React, { useState } from 'react';
import { RSSItem } from '../../services/feedsService';
import './FeedItem.css';

interface FeedItemProps {
  item: RSSItem;
  onToggleRead: (itemId: string, isRead: boolean) => void;
  onToggleStar: (itemId: string, isStarred: boolean) => void;
  onDelete: (itemId: string) => void;
}

const FeedItem: React.FC<FeedItemProps> = ({
  item,
  onToggleRead,
  onToggleStar,
  onDelete
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Unknown date';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const handleToggleExpand = () => {
    setExpanded(!expanded);
    // Mark as read when expanding
    if (!expanded && !item.is_read) {
      onToggleRead(item.item_id, true);
    }
  };

  const handleDelete = () => {
    onDelete(item.item_id);
    setShowDeleteConfirm(false);
  };

  const getContentPreview = (): string => {
    const text = item.description || item.content || '';
    // Strip HTML tags
    const stripped = text.replace(/<[^>]*>/g, '');
    return stripped.length > 200 ? stripped.substring(0, 200) + '...' : stripped;
  };

  const getFullContent = (): string => {
    return item.content || item.description || 'No content available';
  };

  return (
    <div className={`feed-item-card ${item.is_read ? 'read' : 'unread'}`}>
      <div className="feed-item-header" onClick={handleToggleExpand}>
        <div className="feed-item-title-row">
          <h3 className="feed-item-title">
            {item.title}
          </h3>
          <button
            className={`feed-item-star ${item.is_starred ? 'starred' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleStar(item.item_id, !item.is_starred);
            }}
            title={item.is_starred ? 'Unstar' : 'Star'}
          >
            {item.is_starred ? '★' : '☆'}
          </button>
        </div>
        
        <div className="feed-item-meta">
          <span className="feed-item-source">{item.source_name || 'Unknown Source'}</span>
          <span className="feed-item-separator">•</span>
          <span className="feed-item-date">{formatDate(item.published_date)}</span>
          {item.author && (
            <>
              <span className="feed-item-separator">•</span>
              <span className="feed-item-author">{item.author}</span>
            </>
          )}
        </div>

        {!expanded && (
          <p className="feed-item-preview">{getContentPreview()}</p>
        )}
      </div>

      {expanded && (
        <div className="feed-item-content">
          <div 
            className="feed-item-full-content"
            dangerouslySetInnerHTML={{ __html: getFullContent() }}
          />
          
          {item.categories && item.categories.length > 0 && (
            <div className="feed-item-categories">
              {item.categories.map((category, index) => (
                <span key={index} className="feed-item-category">{category}</span>
              ))}
            </div>
          )}

          <div className="feed-item-actions">
            <button
              className="feed-item-action-btn"
              onClick={() => onToggleRead(item.item_id, !item.is_read)}
            >
              {item.is_read ? 'Mark Unread' : 'Mark Read'}
            </button>
            
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="feed-item-action-btn"
              onClick={(e) => e.stopPropagation()}
            >
              Open Link ↗
            </a>

            {!showDeleteConfirm ? (
              <button
                className="feed-item-action-btn delete-btn"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </button>
            ) : (
              <div className="feed-item-delete-confirm">
                <span>Delete this item?</span>
                <button
                  className="feed-item-action-btn confirm-btn"
                  onClick={handleDelete}
                >
                  Yes
                </button>
                <button
                  className="feed-item-action-btn cancel-btn"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  No
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedItem;

